import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { useReportOcupacion } from '@/hooks/useReports'
import { FiltrosReporte } from '@/services/reportService'
import ReportCard from './ReportCard'
import ReportTable from './ReportTable'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface OcupacionReportProps {
  filtros: FiltrosReporte
  onRefresh?: () => void
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <p className="text-gray-600 font-medium">Error al renderizar el gráfico</p>
            <p className="text-sm text-gray-500 mt-2">Intenta actualizar la página</p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const OcupacionReport: React.FC<OcupacionReportProps> = ({ filtros, onRefresh }) => {
  const { colors } = useTheme()
  const { isMobile } = useDeviceDetection()
  const { data: reportData, isLoading, error, refetch } = useReportOcupacion(filtros)

  const handleRefresh = () => {
    refetch()
    onRefresh?.()
  }

  const getErrorMessage = () => {
    if (!error) return undefined
    return error instanceof Error ? error.message : String(error)
  }

  const handleExport = () => {
    if (!reportData?.datos) return

    const csv = [
      ['Período', 'Tipo', 'Reservas', 'Ocupación %'].join(','),
      ...reportData.datos.map((d) => [d.periodo, d.tipo, d.reservas, d.ocupacion_pct].join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ocupacion-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const chartData = reportData?.datos?.map((item, index) => {
    const ocupacion = Number(item.ocupacion_pct) || 0
    // Usar índice como clave única para evitar duplicados en tipos que empiezan igual
    // (ej: Cabaña Familiar, Camping Básico, Camping Premium → todos "CA")
    return {
      periodo: item.periodo,
      tipo: item.tipo, // Nombre completo para tooltip y tabla
      tipoCorto: `T${index + 1}`, // Etiqueta única basada en índice (T1, T2, T3, etc.)
      reservas: Number(item.reservas) || 0,
      ocupacion_pct: Number.isFinite(ocupacion) ? ocupacion : 0
    }
  }) || []

  const tableColumns = [
    { key: 'periodo' as const, label: 'Período' },
    { key: 'tipo' as const, label: 'Tipo de espacio' },
    { key: 'reservas' as const, label: 'Total reservas' },
    {
      key: 'ocupacion_pct' as const,
      label: 'Ocupación %',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return (
          <div className="flex items-center gap-2">
            <span>{validValue.toFixed(1)}%</span>
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${validValue}%`,
                  backgroundColor: validValue >= 70 ? colors.primary : validValue >= 40 ? colors.primaryLight : '#fca5a5'
                }}
              ></div>
            </div>
          </div>
        )
      }
    }
  ]

  return (
    <ReportCard
      title="Ocupación por Tipo"
      subtitle="Distribución de reservas confirmadas por tipo de espacio"
      loading={isLoading}
      error={getErrorMessage()}
      onExport={handleExport}
      chartComponent={
        <ErrorBoundary>
          <div style={{ width: '100%', height: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={isMobile ? 400 : 500}>
              <ComposedChart
                data={chartData}
                margin={isMobile
                  ? { top: 5, right: 10, bottom: 40, left: 10 }
                  : { top: 5, right: 60, bottom: 40, left: 80 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="tipoCorto"
                  angle={0}
                  height={30}
                  tick={false}
                />
                <YAxis
                  yAxisId="left"
                  label={isMobile ? undefined : { value: 'Reservas', angle: -90, position: 'center', offset: 0, dy: -20 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={isMobile ? undefined : { value: 'Ocupación %', angle: 90, position: 'center', offset: 10, dy: 20 }}
                  domain={[0, 100]}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    color: colors.primaryDark,
                    borderRadius: '8px',
                    border: `2px solid ${colors.primary}`,
                    padding: '12px',
                    fontSize: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: any, name: string, props: any) => {
                    if (typeof value === 'number') {
                      // Formatear según el nombre de la serie
                      if (name === 'Ocupación %') {
                        return [value.toFixed(1) + '%', 'Ocupación %']
                      }
                      if (name === 'Reservas') {
                        return [value.toFixed(0), 'Reservas']
                      }
                      return [value.toFixed(0), name]
                    }
                    return [value, name]
                  }}
                  labelFormatter={(label: any) => {
                    // Buscar el item del data para mostrar el nombre completo del tipo
                    const item = chartData.find(d => d.tipoCorto === label)
                    return item ? `Tipo: ${item.tipo}` : `Tipo: ${label}`
                  }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Legend
                  layout={isMobile ? 'horizontal' : 'vertical'}
                  align={isMobile ? 'center' : 'right'}
                  verticalAlign={isMobile ? 'bottom' : 'middle'}
                  wrapperStyle={{ paddingLeft: isMobile ? '0' : '20px', fontSize: isMobile ? '10px' : '12px' }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="reservas"
                  fill={colors.primary}
                  name="Reservas"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="linear"
                  dataKey="ocupacion_pct"
                  stroke={colors.primaryDark}
                  strokeWidth={3}
                  name="Ocupación %"
                  dot={{
                    fill: colors.primaryDark,
                    r: 5
                  }}
                  activeDot={{
                    r: 7,
                    fill: colors.primaryDark
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ErrorBoundary>
      }
      tableComponent={<ReportTable columns={tableColumns} data={chartData} />}
    >
      {reportData?.resumen && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Total reservas</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.total_reservas}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Ocupación promedio</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.ocupacion_promedio.toFixed(1)}%
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Tipos únicos</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.tipos_unicos}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Período</p>
            <p className="text-lg font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.periodo.split(' a ')[0]}
            </p>
          </div>
        </div>
      )}
    </ReportCard>
  )
}

export default OcupacionReport
